import type { ComponentType } from 'react';
import { ServiceDetailPage } from '@/components/ServiceDetailPage';

export type LongDistanceRemovalsPageConfig = {
  path: string;
  navLabel: string;
  cardTitle: string;
  title: string;
  description: string;
  intro: string;
  highlights: string[];
  process: string[];
  idealFor: string[];
};

function createLongDistanceRemovalsPage(config: LongDistanceRemovalsPageConfig): ComponentType {
  return function LongDistanceRemovalsPage() {
    return (
      <ServiceDetailPage
        activePath={config.path}
        serviceMenuPath="/services/long-distance-removals"
        title={config.title}
        description={config.description}
        intro={config.intro}
        highlights={config.highlights}
        process={config.process}
        idealFor={config.idealFor}
      />
    );
  };
}

const baseHighlights = (location: string): string[] => [
  `Long distance removals from ${location} to anywhere across the UK`,
  'Experienced drivers and well-maintained vans equipped for long journeys',
  'Careful loading, secure transit, and safe unloading at your new address',
  'Clear, upfront pricing with no hidden fees for mileage or fuel',
];

const baseProcess = (location: string): string[] => [
  `Share the details of your move from ${location}: destination, dates, property type, and any special items.`,
  'We plan the route and provide a transparent quote tailored to your long distance move.',
  'On the day, our team arrives on time, loads your belongings carefully, and secures everything for the journey.',
  'We transport your items safely to your new home and unload them exactly where you want them.',
];

const baseIdealFor = (location: string): string[] => [
  `Families and individuals relocating from ${location} to another city or region`,
  'Professionals moving long distance for work or lifestyle changes',
  'Anyone wanting a planned, less stressful long distance relocation with a trusted team',
];

export const longDistanceRemovalsPageConfigs: LongDistanceRemovalsPageConfig[] = [
  {
    path: '/services/long-distance-removals/staffordshire',
    navLabel: 'Staffordshire',
    cardTitle: 'Long Distance Removals in Staffordshire',
    title: 'Long Distance Removals in Staffordshire',
    description: 'VIP House Movers provides reliable long distance removals from Staffordshire to anywhere in the UK, with careful planning, clear pricing, and experienced crews.',
    intro: 'Moving long distance from Staffordshire is a big step, and our team is here to make it simple. We plan the journey carefully, protect your belongings every mile, and keep communication clear from the first call to the final box unloaded at your new home.',
    highlights: baseHighlights('Staffordshire'),
    process: baseProcess('Staffordshire'),
    idealFor: baseIdealFor('Staffordshire'),
  },
  {
    path: '/services/long-distance-removals/birmingham',
    navLabel: 'Birmingham',
    cardTitle: 'Long Distance Removals in Birmingham',
    title: 'Long Distance Removals in Birmingham',
    description: 'Dependable long distance removals from Birmingham to destinations across the UK, delivered by VIP House Movers with professional care and transparent pricing.',
    intro: 'Relocating from Birmingham to another part of the country deserves a moving team that understands the details. We combine careful planning, strong logistics, and hands-on customer care so your long distance move feels organised and under control.',
    highlights: baseHighlights('Birmingham'),
    process: baseProcess('Birmingham'),
    idealFor: baseIdealFor('Birmingham'),
  },
  {
    path: '/services/long-distance-removals/wolverhampton',
    navLabel: 'Wolverhampton',
    cardTitle: 'Long Distance Removals in Wolverhampton',
    title: 'Long Distance Removals in Wolverhampton',
    description: 'Based in Wolverhampton, VIP House Movers offers professional long distance removals across the UK with careful handling, clear pricing, and reliable scheduling.',
    intro: 'As a Wolverhampton based removals company, long distance moves are a core part of what we do. We use our local base to plan your journey efficiently and make sure every mile is covered with the same VIP level of care from start to finish.',
    highlights: baseHighlights('Wolverhampton'),
    process: baseProcess('Wolverhampton'),
    idealFor: baseIdealFor('Wolverhampton'),
  },
];

export const longDistanceRemovalsPages = longDistanceRemovalsPageConfigs.map((config) => ({
  ...config,
  component: createLongDistanceRemovalsPage(config),
}));
